import { Component, computed } from '@angular/core';
import { Optional } from '@angular/core';

import { ChatMessage } from '../../core/services/chat.service';
import { TimezoneService } from '../../core/services/timezone.service';

import { ChatWidgetStateService } from '../../core/services/chat-widget-state.service';
import { AiAgentChatStateService } from '../../core/services/ai-agent-chat-state';

@Component({
  selector: 'app-chat',
  standalone: false,
  templateUrl: './chat.html',
  styleUrl: './chat.scss',
})
export class Chat {
  message = '';
  attachmentMenuOpen = false;
  showConversationList = true;

  readonly messageGroups = computed(() => {
    const groups: { id: string; date: string; showDate: boolean; messages: ChatMessage[] }[] = [];
    const timezone = this.timezone.getUserTimezone();
    const day = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
    const label = new Intl.DateTimeFormat(undefined, { timeZone: timezone, dateStyle: 'full' });
    for (const message of this.chatState.thread()) {
      const previous = groups.at(-1);
      const last = previous?.messages.at(-1);
      const date = new Date(message.created_at);
      const sameDay = !!last && day.format(new Date(last.created_at)) === day.format(date);
      const gap = last ? date.getTime() - new Date(last.created_at).getTime() : Infinity;
      if (previous && last && sameDay && last.sender.id === message.sender.id && gap >= 0 && gap <= 5 * 60 * 1000) {
        previous.messages.push(message);
      } else {
        groups.push({ id: message.id, date: label.format(date), showDate: !sameDay, messages: [message] });
      }
    }
    return groups;
  });

  groupTime(message: ChatMessage): string {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: this.timezone.getUserTimezone(), hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    }).format(new Date(message.created_at));
  }

  trackGroup(_index: number, group: { id: string }): string { return group.id; }
  trackMessage(_index: number, message: ChatMessage): string { return message.id; }

  fileSize(bytes: number): string {
    return bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  chooseAttachment(input: HTMLInputElement, accept: string): void {
    this.attachmentMenuOpen = false;
    input.accept = accept;
    input.click();
  }

  toggle(): void {
    if (!this.chatState.open()) this.aiState?.close();
    this.chatState.toggle();
    if (this.chatState.open()) this.showConversationList = true;
  }

  openConversation(conversationId: string): void {
    this.showConversationList = false;
    this.selectConversation(conversationId);
  }

  backToConversationList(): void { this.showConversationList = true; }

  constructor(
    public readonly chatState: ChatWidgetStateService,
    private readonly timezone: TimezoneService,
    @Optional() private readonly aiState: AiAgentChatStateService | null,
  ) {}

  async send(): Promise<void> {
    if (await this.chatState.send(this.message)) {
      this.message = '';
    }
  }

  searchUsers(value: string): void {
    void this.chatState.searchUsers(value);
  }

  selectConversation(conversationId: string): void {
    void this.chatState.selectConversation(conversationId);
  }

  startConversation(userId: string): void {
    this.showConversationList = false;
    void this.chatState.startDirectConversation(userId);
  }

  attach(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) void this.chatState.addAttachment(file);
    input.value = '';
  }

}
